import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { toast } from "sonner";
import { Plus, Trash, X } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/date";

export default function AdminStaff() {
  const { t } = useLang();
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/staff", form);
      toast.success("Staff created");
      setOpen(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this staff member?")) return;
    await api.delete(`/admin/staff/${id}`);
    toast.success("Deleted");
    load();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-4xl">{t.admin.staff}</h1>
        <button data-testid="open-add-staff-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#f4a261] text-[#0b1521] text-sm">
          <Plus size={14} weight="bold" /> {t.admin.addStaff}
        </button>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
        {staff.length === 0 ? (
          <div className="p-10 text-center text-sm text-white/60">No staff yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-white/50 border-b border-white/10">
              <tr>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">Email</th>
                <th className="text-left p-4">Assigned ambassadors</th>
                <th className="text-left p-4">Added</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} data-testid={`staff-row-${s.id}`} className="border-b border-white/5 last:border-0">
                  <td className="p-4">{s.name}</td>
                  <td className="p-4 text-white/70">{s.email}</td>
                  <td className="p-4">{s.assigned_count}</td>
                  <td className="p-4 text-xs text-white/70">{formatDateTime(s.created_at)}</td>
                  <td className="p-4">
                    <button data-testid={`delete-staff-${s.id}`} onClick={() => remove(s.id)} className="text-white/60 hover:text-red-300 inline-flex items-center gap-1 text-xs">
                      <Trash size={14} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="w-full max-w-md rounded-3xl bg-[#122031] p-8 border border-white/10 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/20 flex items-center justify-center"><X size={14} /></button>
            <h2 className="font-serif-display text-3xl">{t.admin.addStaff}</h2>
            <form onSubmit={submit} className="mt-6 space-y-3">
              <input data-testid="staff-name" required placeholder="Full name" value={form.name} onChange={set("name")} className="input" />
              <input data-testid="staff-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="input" />
              <input data-testid="staff-password" required minLength={6} type="text" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} className="input" />
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-5 py-2.5 rounded-full border border-white/20 text-sm">{t.common.cancel}</button>
                <button data-testid="submit-staff-btn" type="submit" className="px-5 py-2.5 rounded-full bg-[#f4a261] text-[#0b1521] text-sm">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .input { width:100%; padding:11px 14px; border-radius:10px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.15); color:white; font-size:14px; outline:none; }
        .input:focus { border-color:#f4a261; }
      `}</style>
    </div>
  );
}
